import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthUser } from '@app/core/models/auth-user';
import { iif, isObservable, Observable, of, Subject } from 'rxjs';

import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  share,
  tap,
} from 'rxjs/operators';
import { Credentials } from '../models/credentials.model';

import { apiUrl } from '@app/app.constants';
import { UserPortalPreferences } from '@app/auth/models/user-portal-preferences';
import { UnauthenticatedHttpClient } from '@app/auth/services/unauthenticated-http-client.service';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { SecurityPermissionName } from '@app/core/models/permission';
import { UserLevel, userLevelIsEnough } from '@app/core/models/user-level';
import { OidcSettings } from '@app/portal/models/app-info';
import { DefaultCache } from '@app/utils/cache';
import { Cached, cached, cachedGroup, RetentionPolicy } from '@app/utils/cached';
import { throwPortalError } from '@app/utils/error/portal-error';
import { Logger, loggerFor } from '@app/utils/logger';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public readonly isAuthenticated$: Observable<boolean>;

  private _authenticated: boolean = false;
  private _authUser: AuthUser | null = null;

  private logger = loggerFor('AuthService');

  private readonly _authUserCached = cached<AuthUser | null>({
    dataProducer: () => this.getAuthUser(),
    id: 'auth-user',
  });

  get authUserCached(): Cached<AuthUser | null> {
    return this._authUserCached;
  }

  /**
   * To be used e.g. by oidc to set the auth-user.
   * If `null` nothing will happen
   * @param au
   */
  setAuthUser(au: AuthUser): void {
    if (au != null) {
      this._authUser = au;
      this._authenticated = true;
      this._authUserCached.setData(au);
    }
  }

  private readonly _authUserData = cached<EndUserData>({
    dataProducer: () => of(apiUrl('/me/ownData')).pipe(mergeMap((url) => this.http.get<EndUserData>(url))),
    id: 'auth-user:own-data',
    retention: RetentionPolicy.Forever,
  });

  get authUserData(): Cached<EndUserData> {
    return this._authUserData;
  }

  get authUserData$(): Observable<EndUserData> {
    return this._authUserData.data$;
  }

  private readonly _authUserPortalPreferences: Cached<UserPortalPreferences | null> = cachedGroup(
    [this._authUserCached],
    ([authUser]) =>
      iif(
        () => authUser != null,
        this.http.get<UserPortalPreferences>(apiUrl('/me/portalPreferences')),
        of(null)
      ),
    {
      id: 'auth-user:portal-preferences',
      retention: RetentionPolicy.Forever,
    }
  );

  /**
   * This only emits auth-user data. If you are interested when the user has logged-in or logged-out
   * then use `isAuthenticated$`.
   */
  public get authUser$(): Observable<AuthUser> {
    return this._authUserCached.data$.pipe(filter((authUser) => authUser != null)) as Observable<AuthUser>;
  }

  public get authUserPortalPreferences(): Cached<UserPortalPreferences | null> {
    return this._authUserPortalPreferences;
  }

  /**
   * Return user snapshot
   */
  public get authUser(): AuthUser | null {
    return this._authUser;
  }

  public get authUserLevel(): UserLevel {
    if (this.userIsSystemAdmin()) {
      return UserLevel.SystemAdmin;
    } else {
      return this.authUser?.role.level;
    }
  }

  /**
   * Return authenticated snapshot
   */
  public get isAuthenticated() {
    return this._authenticated;
  }

  private _onLogout$: Subject<void>;
  readonly onLogout$: Observable<void>;

  constructor(
    private http: HttpClient,
    private unauthenticatedHttp: UnauthenticatedHttpClient
  ) {
    this.isAuthenticated$ = this._authUserCached.data$.pipe(
      map((authUser) => authUser != null),
      distinctUntilChanged()
    );
    this._onLogout$ = new Subject<void>();
    this.onLogout$ = this._onLogout$.pipe(share());

    this.authUser$.subscribe((authUser) => {
      this._authUser = authUser;
    });
  }

  /**
   * Try to authenticate a user with the given credentials
   *
   * @param username login name or phone number of the user
   * @param password Password of the user
   * @returns Observable<AuthUser> if the authentication succeeded, otherwise ErrorObservable
   */
  authenticate(username: string, password: string): Observable<AuthUser> {
    return this.unauthenticatedHttp
      .post<AuthUser>(
        '/middleware/api/login',
        {
          username,
          password,
        },
        { observe: 'response' }
      )
      .pipe(
        map((resp: HttpResponse<AuthUser>): AuthUser => {
          const portalRedirectUrl = resp.headers.get('portal_redirect_url');

          if (portalRedirectUrl != null && window.location.href.includes(portalRedirectUrl) === false) {
            Logger.debug('portal_redirect_url', portalRedirectUrl);
            window.location.replace(portalRedirectUrl);
          }

          return {
            ...resp.body,
            permissions: new Set(resp.body.role.permissions),
          };
        })
      );
  }

  oidcAuthentication(accessToken: string): Observable<AuthUser> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
    });
    return this.unauthenticatedHttp
      .post<AuthUser>('/middleware/api/oidcAuth', null, { headers: headers, observe: 'response' })
      .pipe(
        map((resp: HttpResponse<AuthUser>): AuthUser => {
          const portalRedirectUrl = resp.headers.get('portal_redirect_url');

          if (portalRedirectUrl != null && window.location.href.includes(portalRedirectUrl) === false) {
            Logger.debug('portal_redirect_url', portalRedirectUrl);
            window.location.replace(portalRedirectUrl);
          }

          return {
            ...resp.body,
            permissions: new Set(resp.body.role.permissions),
          };
        })
      );
  }

  // gets OIDC settings information for the `username` from backend, such as: if the oidc is active
  oidcSettings(username: string): Observable<OidcSettings> {
    return this.unauthenticatedHttp.post<OidcSettings>('/middleware/api/oauth', { username });
  }

  /**
   * Clears auth-user and auth-user's preferences (from cache too).
   */
  clearAuthUser() {
    this._authenticated = false;
    this._authUser = null;
    this._authUserCached.setData(null);
    this._authUserData.clear();
    this._authUserPortalPreferences.clear();
  }

  hasPermission(permission: SecurityPermissionName): boolean {
    return this.authUser?.permissions.has(permission) ?? false;
  }

  hasPermissions(...permissions: SecurityPermissionName[]): boolean {
    return permissions.every((p) => this.hasPermission(p));
  }

  hasAnyPermission(...permissions: SecurityPermissionName[]): boolean {
    return permissions.some((p) => this.hasPermission(p));
  }

  hasPermission$(permission: SecurityPermissionName): Observable<boolean> {
    return this.authUser$.pipe(map((au) => au.permissions.has(permission)));
  }

  hasPermissions$(...permissions: SecurityPermissionName[]): Observable<boolean> {
    return this.authUser$.pipe(map((au) => permissions.every((p) => au.permissions.has(p))));
  }

  hasAnyPermission$(...permissions: SecurityPermissionName[]): Observable<boolean> {
    return this.authUser$.pipe(map((au) => permissions.some((p) => au.permissions.has(p))));
  }

  /**
   * Checks whether the currently authenticated user has the required permission, and if so, returns the first
   * observable, otherwise returns the defaultValue wrapped in an observable.
   * @param permission Permission to check
   * @param true$ Observable to use if the user has the permission
   * @param defaultValue Value that is wrapped in an observable and used if the user does not have the permission
   */
  ifPermission$<T>(permission: SecurityPermissionName, true$: Observable<T>, defaultValue: T): Observable<T>;

  /**
   * Checks whether the currently authenticated user has the required permission, and if so, returns the first
   * observable, otherwise returns the defaultValue$.
   * @param permission Permission to check
   * @param true$ Observable to use if the user has the permission
   * @param defaultValue$ Observable to use if the user does not have the permission
   */
  ifPermission$<T>(
    permission: SecurityPermissionName,
    true$: Observable<T>,
    defaultValue$: Observable<T>
  ): Observable<T>;

  ifPermission$<T>(
    permission: SecurityPermissionName,
    true$: Observable<T>,
    defaultValue: T | Observable<T>
  ): Observable<T> {
    return iif(
      () => this.hasPermission(permission),
      true$,
      isObservable(defaultValue) ? defaultValue : of(defaultValue)
    );
  }

  /**
   * Checks whether the currently authenticated user has at least one of the given permissions, and if so, returns the
   * first observable, otherwise returns the defaultValue wrapped in an observable.
   * @param permissions Permission to check
   * @param true$ Observable to use if the user has the permission
   * @param defaultValue Value that is wrapped in an observable and used if the user does not have the permission
   */
  ifAnyPermission$<T>(
    permissions: SecurityPermissionName[],
    true$: Observable<T>,
    defaultValue: T
  ): Observable<T>;

  /**
   * Checks whether the currently authenticated user has at least one of the given permissions, and if so, returns the
   * first observable, otherwise returns the defaultValue$.
   * @param permissions Permission to check
   * @param true$ Observable to use if the user has the permission
   * @param defaultValue$ Observable to use if the user does not have the permission
   */
  ifAnyPermission$<T>(
    permissions: SecurityPermissionName[],
    true$: Observable<T>,
    defaultValue$: Observable<T>
  ): Observable<T>;

  ifAnyPermission$<T>(
    permissions: SecurityPermissionName[],
    true$: Observable<T>,
    defaultValue: T | Observable<T>
  ): Observable<T> {
    return iif(
      () => this.hasAnyPermission(...permissions),
      true$,
      isObservable(defaultValue) ? defaultValue : of(defaultValue)
    );
  }

  loginUser(credentials: Credentials): Observable<AuthUser> {
    return this.authenticate(credentials.username, credentials.password).pipe(
      tap((user) => {
        this._authUser = user;
        this._authenticated = true;
        this._authUserCached.setData(user);
      })
    );
  }

  /**
   * Clears and reloads auth-user own-data from backend
   */
  reloadAuthUserOwnData(): void {
    this._authUserData.clear();
    this._authUserData.reload();
  }

  userIsEndUser() {
    return this.userIs(UserLevel.EndUser);
  }

  userIsEnterpriseAdmin() {
    return this.userIs(UserLevel.EnterpriseAdmin);
  }

  userIsServiceProviderAdmin() {
    return this.userIs(UserLevel.ServiceProviderAdmin);
  }

  userIsSystemAdmin() {
    return (
      this.userIs(UserLevel.SystemAdmin) ||
      (this.userIsServiceProviderAdmin() && this.hasPermission(SecurityPermissionName.MANAGE_SYSTEM))
    );
  }

  userIs(userLevel: UserLevel) {
    return this.isAuthenticated && this.authUser.role.level === userLevel;
  }

  userMinimalLevel(userLevel: UserLevel): boolean {
    return this.isAuthenticated && userLevelIsEnough(userLevel, this.authUser.role.level);
  }

  /**
   * Clear the authentication
   */
  logout(): Observable<string> {
    return this.http
      .post<string>('/middleware/api/logout', {
        loginName: '',
        responseType: 'text',
      })
      .pipe(
        catchError(() => of('bye')),
        finalize(() => {
          this._authenticated = false;
          this._authUser = null;

          // Unintended side effects could happen upon a fast logout/login if the cache isn't cleared
          // Also regarding privacy issues this should absolutely be done
          DefaultCache().clear();

          this._onLogout$.next();
        })
      );
  }

  /**
   * This performs simple user session validation at back-end.
   * A use case would be to check if the session has expired.
   *
   * Note: nothing to do here if the session becomes invalid. The HTTP interceptor will take care of it.
   * @returns nothing
   */
  requestUserSessionValidation(): Observable<void> {
    return of(apiUrl('/login')).pipe(
      mergeMap((url) => this.http.head<void>(url)),
      catchError((err) => {
        if (err.status && err.status === 401) {
          return of(null);
        }

        this.logger.error('isUserSessionValid', err);
        return throwPortalError({
          cause: err,
          id: 'isUserSessionValid',
        });
      })
    );
  }

  // this will be tried immediately on app initialization
  private getAuthUser(): Observable<AuthUser | null> {
    return of(apiUrl('/login')).pipe(
      mergeMap((url) => this.http.get<AuthUser>(url)),
      map((au) => {
        au.permissions = new Set(au.role.permissions);
        return au;
      }),
      tap((authentication) => {
        this._authenticated = authentication !== undefined;
      }),
      catchError((err) => {
        if (err.status && err.status === 401) {
          return of(null);
        }

        this.logger.error('getAuthUserExtended', err);
        return throwPortalError({
          cause: err,
          id: 'getAuthUserExtended',
        });
      })
    );
  }
}
